/**
 * BrandKitModal — create or edit a Vivideo brand kit.
 *
 * Two-panel studio: the form on the left, a LIVE brand preview on the right —
 * a mock video frame that re-renders with the chosen palette, real fonts
 * (loaded from Google Fonts on the fly), the uploaded logo as a corner
 * watermark, and the motion style. What you see is what the agent is told.
 *
 * A kit = logo (asset library → HeyGen asset id + URL), 4-color palette,
 * title/body fonts, motion-graphics style, and a free-text BRAND IDENTITY
 * (persona, target audience, value propositions).
 *
 * Applied invisibly at generation time: the backend attaches the logo and
 * injects the guidelines into the hidden system-instruction section — the
 * user only ever sees "brand selected". (Reverse-engineered HeyGen brand
 * system; validated by experiment to match/beat the native one.)
 */
import { useEffect, useState } from 'react';
import { trackAmpBrandKitCreated } from '@/services/amplitudeService';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Sparkles, ImagePlus, Landmark, Play, Link2, Wand2 } from 'lucide-react';
import { cn } from '@/v2/utils';
import { v2Service } from '@/api/v2Service';
import { isFreeLimitError, isCreditDeclined, useUpgradeModal } from '@/v2/upgradeModal';
import { useUserStore } from '@/store/userStore';
import { inferPaletteFromLogo } from '@/v2/logoPalette';
import { toast } from '@/v2/toast';
import type { V2Brand, V2MotionStyle } from '@/api/v2Types';

const MOTION_STYLES: { id: V2MotionStyle; hint: string }[] = [
  { id: 'elevated', hint: 'refined & premium' },
  { id: 'friendly', hint: 'warm & rounded' },
  { id: 'minimal', hint: 'clean & quiet' },
  { id: 'professional', hint: 'crisp & corporate' },
  { id: 'playful', hint: 'bouncy & bold' },
];

// All Google Fonts — so the live preview renders the real typefaces.
const FONTS = [
  'Alice', 'Playfair Display', 'DM Serif Display', 'Merriweather', 'Lora',
  'Roboto Slab', 'Inter', 'Montserrat', 'Poppins', 'Oswald',
];

const COLOR_DEFS = [
  { key: 'primary_color', label: 'Primary' },
  { key: 'secondary_color', label: 'Secondary' },
  { key: 'tertiary_color', label: 'Tertiary' },
  { key: 'accent_color', label: 'Accent' },
] as const;

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50 focus:bg-white/[0.07] transition-colors';

// Load a Google Font once for the live preview (dedupe per family).
const loadedFonts = new Set<string>();
function ensureFont(family: string) {
  if (loadedFonts.has(family)) return;
  loadedFonts.add(family);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;700&display=swap`;
  document.head.appendChild(link);
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-bold uppercase tracking-widest text-text-tertiary mb-3">{children}</p>;
}

export function BrandKitModal({ existing, onClose, onSaved }: {
  /** Pass a kit to edit it; omit to create a new one. */
  existing?: V2Brand;
  onClose: () => void;
  onSaved?: (b: V2Brand) => void;
}) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  // Premium gating: creating a brand kit (and the URL autofill, which costs an
  // LLM call) is paid-only. Free users are redirected to upgrade at the CTA.
  const isPaid = useUserStore((s) => s.isPro());
  const [name, setName] = useState(existing?.name || '');
  const [logoUrl, setLogoUrl] = useState(existing?.logo_url || '');
  const [logoAssetId, setLogoAssetId] = useState(existing?.logo_asset_id || '');
  const [colors, setColors] = useState<Record<string, string>>({
    primary_color: existing?.primary_color || '#2D1B4A',
    secondary_color: existing?.secondary_color || '#F5F1E8',
    tertiary_color: existing?.tertiary_color || '#A3B18A',
    accent_color: existing?.accent_color || '#C9A86A',
  });
  const [titleFont, setTitleFont] = useState(existing?.title_font || 'Alice');
  const [bodyFont, setBodyFont] = useState(existing?.body_font || 'Roboto Slab');
  const [motion, setMotion] = useState<V2MotionStyle>(existing?.motion_style || 'elevated');
  const [identity, setIdentity] = useState(existing?.brand_identity || '');
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // URL → brand kit (Feature 2)
  const [url, setUrl] = useState('');
  const [urlBusy, setUrlBusy] = useState(false);

  // Live-load the chosen typefaces so the preview is real.
  useEffect(() => { ensureFont(titleFont); }, [titleFont]);
  useEffect(() => { ensureFont(bodyFont); }, [bodyFont]);

  const uploadLogo = async (file: File | null) => {
    if (!file || !userId) return;
    setErr(null);
    // Infer a palette straight from the logo so the colour pickers pre-fill —
    // runs on the local file (no CORS), in parallel with the upload. The user
    // can still adjust every swatch afterwards.
    void inferPaletteFromLogo(file).then((p) => { if (p) setColors((c) => ({ ...c, ...p })); });
    try {
      setUploadPct(1);
      // Asset library upload → HeyGen asset id (attachable) + public URL (preview)
      const asset = await v2Service.uploadAsset(userId, file, (p) => setUploadPct(p));
      setLogoAssetId(asset.asset_id);
      setLogoUrl(asset.url || '');
    } catch {
      setErr(t('brandKit.logoUploadFailed'));
    } finally {
      setUploadPct(null);
    }
  };

  // Explore a URL and pre-fill every field — the user reviews before creating.
  const autofillFromUrl = async () => {
    const u = url.trim();
    if (!u || !userId || urlBusy) return;
    if (!isPaid) { useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset: 'brand' }); return; }
    setUrlBusy(true); setErr(null);
    try {
      const b = await v2Service.brandFromUrl(userId, u);
      if (b.name) setName(b.name);
      if (b.logo_url) setLogoUrl(b.logo_url);
      if (b.logo_asset_id) setLogoAssetId(b.logo_asset_id);
      setColors((c) => ({
        primary_color: b.primary_color || c.primary_color,
        secondary_color: b.secondary_color || c.secondary_color,
        tertiary_color: b.tertiary_color || c.tertiary_color,
        accent_color: b.accent_color || c.accent_color,
      }));
      if (b.title_font && FONTS.includes(b.title_font)) setTitleFont(b.title_font);
      if (b.body_font && FONTS.includes(b.body_font)) setBodyFont(b.body_font);
      if (b.motion_style && MOTION_STYLES.some((m) => m.id === b.motion_style)) setMotion(b.motion_style);
      if (b.brand_identity) setIdentity(b.brand_identity);
      toast.success(t('brandKit.draftedFromUrl'));
    } catch {
      setErr(t('brandKit.couldNotReadUrl'));
    } finally {
      setUrlBusy(false);
    }
  };

  const save = async () => {
    if (!userId || !name.trim() || busy) return;
    // Only NEW kits are gated (editing an existing kit is free). Free users are
    // redirected to upgrade instead of hitting the backend.
    if (!existing && !isPaid) { useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset: 'brand' }); return; }
    setBusy(true); setErr(null);
    const fields = {
      name: name.trim(),
      logo_asset_id: logoAssetId || undefined,
      logo_url: logoUrl || undefined,
      ...colors,
      title_font: titleFont,
      body_font: bodyFont,
      motion_style: motion,
      brand_identity: identity.trim() || undefined,
    };
    try {
      const saved = existing
        ? await v2Service.updateBrand(userId, existing.brand_ref, fields)
        : await v2Service.createBrand(userId, fields as typeof fields & { name: string });
      if (!existing) trackAmpBrandKitCreated();
      onSaved?.(saved);
      onClose();
    } catch (e) {
      // Free-tier popup, or the user declined the credit charge → close silently.
      if (isFreeLimitError(e) || isCreditDeclined(e)) { onClose(); return; }
      setErr(t('brandKit.couldNotSave'));
    } finally {
      setBusy(false);
    }
  };

  const c = colors;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div
        className="w-full max-w-5xl max-h-[92vh] flex flex-col rounded-3xl bg-[#15151d] border border-white/10 shadow-2xl animate-slide-up overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 px-7 py-5 border-b border-white/10 flex items-center gap-4">
          <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary/30 to-accent/30 border border-primary/30 flex items-center justify-center shrink-0">
            <Landmark className="w-5 h-5 text-primary" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-text-primary tracking-tight">{existing ? t('brandKit.editTitle') : t('brandKit.newTitle')}</h2>
            <p className="text-xs text-text-tertiary mt-0.5">{t('brandKit.subtitle')}</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-0">
            {/* ============ LEFT: the form ============ */}
            <div className="px-7 py-6 space-y-7 lg:border-r lg:border-white/10">
              {/* URL → brand kit: explore a site and pre-fill everything */}
              <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 to-accent/5 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 className="w-4 h-4 text-primary" />
                  <span className="text-sm font-bold text-text-primary">{t('brandKit.startFromUrl')}</span>
                  <span className="px-1.5 py-0.5 rounded bg-primary/20 text-primary text-[9px] font-bold uppercase tracking-wide">{t('brandKit.newBadge')}</span>
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
                    <input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') void autofillFromUrl(); }}
                      placeholder={t('brandKit.urlPlaceholder')}
                      className={cn(inputCls, 'pl-9')}
                    />
                  </div>
                  <button
                    onClick={() => void autofillFromUrl()}
                    disabled={urlBusy || !url.trim()}
                    className="shrink-0 px-4 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold flex items-center gap-1.5 hover:opacity-95 disabled:opacity-50 transition-all"
                  >
                    {urlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {urlBusy ? t('brandKit.reading') : t('brandKit.autoFill')}
                  </button>
                </div>
                <p className="text-[11px] text-text-tertiary mt-2">{t('brandKit.urlHelp')}</p>
              </div>

              {/* Name + logo */}
              <div className="flex gap-5 items-start">
                <label className="shrink-0 cursor-pointer group">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => void uploadLogo(e.target.files?.[0] ?? null)} />
                  <span className={cn(
                    'w-24 h-24 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-1 overflow-hidden transition-all',
                    logoUrl ? 'border-white/10' : 'border-white/15 group-hover:border-primary/60 group-hover:bg-primary/5 text-text-tertiary group-hover:text-primary'
                  )}>
                    {uploadPct !== null ? (
                      <><Loader2 className="w-5 h-5 animate-spin text-primary" /><span className="text-[9px] font-semibold">{uploadPct}%</span></>
                    ) : logoUrl ? (
                      <img src={logoUrl} alt={t('brandKit.logoAlt')} className="w-full h-full object-contain bg-white/5" />
                    ) : (
                      <><ImagePlus className="w-5 h-5" /><span className="text-[10px] font-semibold">{t('brandKit.logo')}</span></>
                    )}
                  </span>
                </label>
                <div className="flex-1 space-y-2">
                  <label className="block text-sm font-semibold text-text-primary">{t('brandKit.brandName')}</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('brandKit.brandNamePlaceholder')} className={inputCls} />
                  {logoUrl && (
                    <button onClick={() => { setLogoUrl(''); setLogoAssetId(''); }} className="text-[11px] text-text-tertiary hover:text-text-primary transition-colors">
                      {t('brandKit.removeLogo')}
                    </button>
                  )}
                </div>
              </div>

              {/* Colors */}
              <div>
                <SectionLabel>{t('brandKit.colorPalette')}</SectionLabel>
                <div className="grid grid-cols-2 gap-3">
                  {COLOR_DEFS.map(({ key }) => (
                    <label key={key} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 cursor-pointer hover:border-primary/30 transition-colors">
                      <span className="relative">
                        <input
                          type="color"
                          value={colors[key]}
                          onChange={(e) => setColors((p) => ({ ...p, [key]: e.target.value.toUpperCase() }))}
                          className="w-10 h-10 rounded-full border-none bg-transparent cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-none"
                        />
                        <span className="absolute inset-0 rounded-full ring-1 ring-white/20 pointer-events-none" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-xs font-bold text-text-primary">{t(`brandKit.color_${key}`)}</span>
                        <span className="block text-[10px] text-text-tertiary uppercase tracking-wide">{colors[key]}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Fonts */}
              <div>
                <SectionLabel>{t('brandKit.typography')}</SectionLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-2">{t('brandKit.titleText')}</label>
                    <select value={titleFont} onChange={(e) => setTitleFont(e.target.value)} className={inputCls}>
                      {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-text-secondary mb-2">{t('brandKit.bodyText')}</label>
                    <select value={bodyFont} onChange={(e) => setBodyFont(e.target.value)} className={inputCls}>
                      {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Motion graphics */}
              <div>
                <SectionLabel>{t('brandKit.motionGraphics')}</SectionLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {MOTION_STYLES.map(({ id }) => (
                    <button key={id} type="button" onClick={() => setMotion(id)}
                      className={cn(
                        'flex items-center justify-between px-4 py-2.5 rounded-xl text-left transition-all',
                        motion === id
                          ? 'bg-primary/15 border border-primary/40'
                          : 'bg-white/5 border border-transparent hover:bg-white/10'
                      )}>
                      <span className={cn('text-sm font-semibold capitalize', motion === id ? 'text-primary' : 'text-text-primary')}>{t(`brandKit.motion_${id}`)}</span>
                      <span className="text-[10px] text-text-tertiary">{t(`brandKit.motionHint_${id}`)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand identity */}
              <div>
                <SectionLabel>{t('brandKit.brandIdentity')}</SectionLabel>
                <p className="text-xs text-text-tertiary mb-2 -mt-1">{t('brandKit.brandIdentityHelp')}</p>
                <textarea
                  rows={4}
                  maxLength={2000}
                  value={identity}
                  onChange={(e) => setIdentity(e.target.value)}
                  placeholder={t('brandKit.brandIdentityPlaceholder')}
                  className={cn(inputCls, 'resize-none')}
                />
                <p className="text-[10px] text-text-tertiary mt-1 text-right">{identity.length}/2000</p>
              </div>
            </div>

            {/* ============ RIGHT: live brand preview ============ */}
            <div className="px-7 py-6 bg-black/20">
              <SectionLabel>{t('brandKit.livePreview')}</SectionLabel>

              {/* Mock video frame */}
              <div
                className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
                style={{ background: `linear-gradient(135deg, ${c.primary_color} 0%, ${c.primary_color}E6 60%, #000 140%)` }}
              >
                {/* faux b-roll texture */}
                <div className="absolute inset-0 opacity-[0.07]" style={{
                  backgroundImage: `radial-gradient(circle at 30% 20%, ${c.secondary_color} 0, transparent 40%), radial-gradient(circle at 80% 70%, ${c.tertiary_color} 0, transparent 45%)`,
                }} />

                {/* logo watermark */}
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="absolute top-3 right-3 w-9 h-9 rounded-lg object-contain bg-black/20 p-1" />
                ) : (
                  <span className="absolute top-3 right-3 w-9 h-9 rounded-lg bg-black/25 border border-white/15 flex items-center justify-center text-[8px] font-bold" style={{ color: c.secondary_color }}>
                    {t('brandKit.logoWatermark')}
                  </span>
                )}

                {/* headline (title font) */}
                <div className="absolute left-5 top-1/4 max-w-[70%]">
                  <p className="text-2xl sm:text-3xl font-bold leading-tight" style={{ fontFamily: `'${titleFont}', serif`, color: c.secondary_color }}>
                    {name.trim() || t('brandKit.previewHeadline')}
                  </p>
                  <p className="text-sm mt-1" style={{ fontFamily: `'${titleFont}', serif`, color: c.accent_color }}>
                    {t('brandKit.previewTagline')}
                  </p>
                </div>

                {/* play affordance */}
                <span className="absolute inset-0 m-auto w-12 h-12 rounded-full flex items-center justify-center pointer-events-none"
                  style={{ backgroundColor: `${c.secondary_color}26`, border: `1.5px solid ${c.secondary_color}66` }}>
                  <Play className="w-5 h-5 ml-0.5" style={{ color: c.secondary_color, fill: c.secondary_color }} />
                </span>

                {/* caption lower-third (body font) */}
                <div className="absolute left-4 right-4 bottom-4">
                  <div className="inline-block px-3 py-2 rounded-lg shadow-lg" style={{ backgroundColor: `${c.primary_color}F2`, border: `1px solid ${c.secondary_color}1F` }}>
                    <p className="text-[11px] leading-snug" style={{ fontFamily: `'${bodyFont}', sans-serif`, color: c.secondary_color }}>
                      {t('brandKit.previewCaptionBefore')}<span style={{ color: c.accent_color, fontWeight: 700 }}>{t('brandKit.previewCaptionEmphasis')}</span>{t('brandKit.previewCaptionAfter')}
                    </p>
                  </div>
                </div>
              </div>

              {/* palette strip */}
              <div className="flex items-center gap-2 mt-4">
                {COLOR_DEFS.map(({ key }) => (
                  <div key={key} className="flex-1 group">
                    <div className="h-9 rounded-xl ring-1 ring-white/10 transition-transform group-hover:-translate-y-0.5" style={{ backgroundColor: colors[key] }} />
                    <p className="text-[9px] text-text-tertiary text-center mt-1 uppercase tracking-wide">{t(`brandKit.color_${key}`)}</p>
                  </div>
                ))}
              </div>

              {/* type specimen */}
              <div className="mt-4 p-4 rounded-2xl bg-white/[0.04] border border-white/10 space-y-1.5">
                <p className="text-xl text-text-primary" style={{ fontFamily: `'${titleFont}', serif` }}>{t('brandKit.specimenTitle', { font: titleFont })}</p>
                <p className="text-sm text-text-secondary" style={{ fontFamily: `'${bodyFont}', sans-serif` }}>{t('brandKit.specimenBody', { font: bodyFont })}</p>
              </div>

            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-7 py-4 border-t border-white/10 flex items-center gap-3">
          {err && <p className="text-xs text-amber-300/80 flex-1">{err}</p>}
          {!err && <span className="flex-1" />}
          <button onClick={onClose} className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors">
            {t('brandKit.cancel')}
          </button>
          <button onClick={() => void save()} disabled={busy || !name.trim() || uploadPct !== null}
            className="px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-95 hover:shadow-glow disabled:opacity-50 transition-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? t('brandKit.saving') : existing ? t('brandKit.saveChanges') : t('brandKit.createBrandKit')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
